import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Wallet, CreditCard, CheckCircle, ArrowRight, ArrowLeft, Package, Sparkle, ShieldCheck, Trash } from "@phosphor-icons/react";

const readItems = (sp) => {
  const items = [];
  if (sp.get("device")) items.push({ kind: "device", ref: sp.get("device"), qty: 1 });
  if (sp.get("plan")) items.push({ kind: "plan", ref: sp.get("plan"), qty: 1 });
  if (sp.get("wireless")) items.push({ kind: "wireless", ref: sp.get("wireless"), qty: 1, billing: sp.get("billing") === "annual" ? "annual" : "monthly" });
  return items;
};

const STEPS = ["Review", "Payment", "Confirm"];

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <img src="/icons/icon-512.png" alt="iYeneh Connect" className="h-9 w-9" />
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>iYeneh Connect</span>
          </Link>
          <Link to="/shop" className="text-sm font-semibold text-muted-foreground hover:text-foreground">Continue shopping</Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">{children}</main>
    </div>
  );
}

export default function Checkout() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const sessionId = sp.get("session_id");

  const [items, setItems] = useState(() => readItems(sp));
  const [quote, setQuote] = useState(null);
  const [config, setConfig] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState("wallet");
  const [ship, setShip] = useState({ name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [checking, setChecking] = useState(!!sessionId);
  const [suggest, setSuggest] = useState([]);
  const [plansForUpsell, setPlansForUpsell] = useState([]);

  // Stripe return
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false, tries = 0;
    const tick = async () => {
      tries++;
      try {
        const { data } = await api.get(`/telecom/checkout/stripe/status/${sessionId}`);
        if (data.paid) { if (!cancelled) { setDone({ reference: data.reference }); setChecking(false); } return; }
      } catch (e) { /* keep polling */ }
      if (tries < 12 && !cancelled) setTimeout(tick, 1500);
      else if (!cancelled) setChecking(false);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => { api.get("/telecom/checkout/config").then(({ data }) => setConfig(data)).catch(() => {}); }, []);

  useEffect(() => {
    if (sessionId || !items.length) { setQuote(null); return; }
    api.post("/telecom/checkout/quote", { items }).then(({ data }) => setQuote(data)).catch((e) => toast.error(apiError(e)));
  }, [items, sessionId]);

  useEffect(() => {
    if (user) api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  }, [user, done]);

  const hasPlan = items.some((i) => i.kind === "plan" || i.kind === "wireless");
  const hasDevice = items.some((i) => i.kind === "device");

  // Upsell data
  useEffect(() => {
    if (!hasDevice && hasPlan) {
      api.get("/telecom/shop/catalog").then(({ data }) =>
        setSuggest((data.products || []).filter((p) => p.featured).slice(0, 3))).catch(() => {});
    }
    if (hasDevice && !hasPlan) {
      api.get("/telecom/plans").then(({ data }) => setPlansForUpsell(data.plans || [])).catch(() => {});
    }
  }, [hasDevice, hasPlan]);

  const addItem = (it) => setItems((cur) => cur.some((x) => x.kind === it.kind && x.ref === it.ref) ? cur : [...cur, it]);
  const removeItem = (idx) => setItems((cur) => cur.filter((_, i) => i !== idx));

  const total = quote?.total_cents ?? 0;
  const walletCents = wallet?.balance_cents ?? 0;
  const walletShort = total > walletCents;
  const needAddr = hasDevice && (method === "wallet" || method === "paypal");
  const addrValid = !needAddr || !!(ship.name && ship.line1 && ship.city && ship.postal_code && ship.country);

  const requireAuth = () => {
    if (user) return true;
    try { localStorage.setItem("iyeneh_pending_checkout", JSON.stringify(items)); } catch (e) {}
    toast.message("Please sign in or create your free account to pay");
    navigate("/login");
    return false;
  };

  const payWallet = async () => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/telecom/checkout/pay/wallet", { items, shipping: needAddr ? ship : undefined });
      setDone({ reference: data.reference });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const payStripe = async () => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/telecom/checkout/pay/stripe", { items, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  // ---------- SUCCESS ----------
  if (done || (sessionId && !checking && done)) {
    return (
      <Shell>
        <div className="mx-auto max-w-lg rounded-3xl border border-border bg-card p-8 text-center" data-testid="checkout-success">
          <CheckCircle size={56} weight="fill" className="mx-auto text-emerald-500" />
          <h1 className="mt-4 font-display text-2xl font-bold">Payment complete</h1>
          <p className="mt-2 text-muted-foreground">Thank you! Your order is confirmed{done?.reference ? ` (ref ${done.reference})` : ""}. A receipt has been recorded on your account.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/app" data-testid="success-account" className="rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground">Go to my account</Link>
            <Link to="/shop" className="rounded-full border border-border px-6 py-3 text-sm font-semibold">Keep shopping</Link>
          </div>
        </div>
      </Shell>
    );
  }
  if (sessionId && checking) {
    return <Shell><div className="py-20 text-center text-muted-foreground" data-testid="checkout-verifying">Confirming your payment…</div></Shell>;
  }

  if (!items.length) {
    return (
      <Shell>
        <div className="rounded-2xl border border-dashed border-border p-12 text-center">
          <Package size={40} className="mx-auto text-muted-foreground/60" />
          <p className="mt-3 font-semibold">Your cart is empty</p>
          <Link to="/shop" className="mt-3 inline-flex text-sm font-bold text-primary">Browse devices</Link>
        </div>
      </Shell>
    );
  }

  const Stepper = () => (
    <div className="mb-8 flex items-center gap-2" data-testid="checkout-stepper">
      {STEPS.map((s, i) => {
        const n = i + 1, active = step === n, complete = step > n;
        return (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 ${active ? "text-primary" : complete ? "text-emerald-500" : "text-muted-foreground"}`}>
              <span className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs font-bold ${active ? "border-primary bg-primary text-primary-foreground" : complete ? "border-emerald-500 text-emerald-500" : "border-border"}`}>{complete ? "✓" : n}</span>
              <span className="hidden text-sm font-semibold sm:block">{s}</span>
            </div>
            {i < STEPS.length - 1 && <span className="h-px flex-1 bg-border" />}
          </React.Fragment>
        );
      })}
    </div>
  );

  const Summary = () => (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="checkout-summary">
      <h3 className="font-semibold">Order summary</h3>
      <div className="mt-3 space-y-3">
        {(quote?.lines || []).map((l, i) => (
          <div key={i} className="flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0"><span className="font-semibold">{l.name}</span>{l.qty > 1 ? ` ×${l.qty}` : ""}{l.discount_cents ? <span className="ml-1 text-xs font-semibold text-emerald-500">-{quote.bundle_pct}%</span> : ""}</span>
            <span className="font-mono-metric">{money(l.line_total_cents - l.discount_cents, "USD")}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 space-y-1 border-t border-border pt-3 text-sm">
        <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{money(quote?.subtotal_cents || 0, "USD")}</span></div>
        {quote?.discount_cents ? <div className="flex justify-between text-emerald-500"><span>Bundle savings</span><span>-{money(quote.discount_cents, "USD")}</span></div> : null}
        <div className="flex justify-between pt-1 text-base font-bold"><span>Total</span><span data-testid="checkout-total">{money(total, "USD")}</span></div>
      </div>
      {hasPlan && <p className="mt-3 text-xs text-muted-foreground">Plans charge the first month now, then auto-renew monthly from your iYeneh Wallet. Cancel anytime.</p>}
    </div>
  );

  return (
    <Shell>
      <h1 className="mb-6 font-display text-3xl font-bold tracking-tight">Checkout</h1>
      <Stepper />
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          {/* STEP 1 REVIEW */}
          {step === 1 && (
            <div className="space-y-5" data-testid="checkout-step-review">
              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="mb-3 font-semibold">Your items</h3>
                <div className="space-y-3">
                  {items.map((it, i) => {
                    const line = (quote?.lines || [])[i];
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-secondary">{line?.image ? <img src={line.image} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-muted-foreground/40"><Package size={20} /></div>}</div>
                        <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{line?.name || it.ref}</p><p className="text-xs text-muted-foreground">{money((line?.unit_cents || 0), "USD")}{it.kind !== "device" ? (it.billing === "annual" ? " / yr" : " / mo") : ""}</p></div>
                        <button onClick={() => removeItem(i)} data-testid={`cart-remove-${i}`} className="text-muted-foreground hover:text-red-500"><Trash size={16} /></button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* BUNDLE UPSELL */}
              {hasDevice && !hasPlan && plansForUpsell.length > 0 && (
                <div className="rounded-2xl border border-primary/40 bg-primary/5 p-5" data-testid="upsell-plans">
                  <div className="mb-2 flex items-center gap-2 text-primary"><Sparkle size={18} weight="fill" /><span className="text-sm font-bold">Bundle & save {quote?.bundle_pct || 10}% on your device</span></div>
                  <p className="mb-3 text-sm text-muted-foreground">Add a phone plan and we'll take {quote?.bundle_pct || 10}% off your device instantly.</p>
                  <div className="flex flex-wrap gap-2">
                    {plansForUpsell.map((p) => (
                      <button key={p.key} onClick={() => addItem({ kind: "plan", ref: p.key, qty: 1 })} data-testid={`add-plan-${p.key}`}
                        className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold hover:border-primary">
                        {p.name} — {money(p.price_cents, "USD")}/mo
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {hasPlan && !hasDevice && suggest.length > 0 && (
                <div className="rounded-2xl border border-primary/40 bg-primary/5 p-5" data-testid="upsell-devices">
                  <div className="mb-2 flex items-center gap-2 text-primary"><Sparkle size={18} weight="fill" /><span className="text-sm font-bold">Add a device & save {quote?.bundle_pct || 10}%</span></div>
                  <div className="grid grid-cols-3 gap-3">
                    {suggest.map((p) => (
                      <button key={p.id} onClick={() => addItem({ kind: "device", ref: p.id, qty: 1 })} data-testid={`add-device-${p.id}`}
                        className="rounded-xl border border-border bg-card p-2 text-left hover:border-primary">
                        <div className="aspect-square w-full overflow-hidden rounded-lg bg-secondary">{p.image ? <img src={p.image} alt="" className="h-full w-full object-cover" /> : null}</div>
                        <p className="mt-1 line-clamp-1 text-xs font-semibold">{p.name}</p>
                        <p className="text-xs text-primary">{money(p.price_cents, "USD")}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={() => setStep(2)} data-testid="to-payment" className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-primary-foreground">Continue to payment <ArrowRight size={16} weight="bold" /></button>
            </div>
          )}

          {/* STEP 2 PAYMENT METHOD */}
          {step === 2 && (
            <div className="space-y-4" data-testid="checkout-step-payment">
              <h3 className="font-semibold">Choose how to pay</h3>
              {[
                { k: "wallet", icon: Wallet, t: "iYeneh Wallet", d: user ? `Balance ${money(walletCents, "USD")}${walletShort ? " — not enough, top up or use another method" : ""}` : "Sign in to use your wallet balance", disabled: user && walletShort },
                { k: "card", icon: CreditCard, t: "Credit / Debit Card", d: "Secure card payment" },
                ...(config?.paypal_enabled ? [{ k: "paypal", icon: CreditCard, t: "PayPal", d: "Pay with your PayPal account" }] : []),
              ].map((m) => (
                <button key={m.k} onClick={() => !m.disabled && setMethod(m.k)} disabled={m.disabled} data-testid={`method-${m.k}`}
                  className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition-colors disabled:opacity-50 ${method === m.k ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                  <m.icon size={22} weight="duotone" className="text-primary" />
                  <div className="flex-1"><p className="text-sm font-bold">{m.t}</p><p className="text-xs text-muted-foreground">{m.d}</p></div>
                  <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${method === m.k ? "border-primary bg-primary" : "border-border"}`}>{method === m.k && <span className="h-2 w-2 rounded-full bg-primary-foreground" />}</span>
                </button>
              ))}
              <div className="flex gap-2 pt-2">
                <button onClick={() => setStep(1)} className="inline-flex items-center gap-1 rounded-full border border-border px-5 py-3 text-sm font-semibold"><ArrowLeft size={15} /> Back</button>
                <button onClick={() => setStep(3)} data-testid="to-confirm" className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-bold text-primary-foreground">Review & pay <ArrowRight size={16} weight="bold" /></button>
              </div>
            </div>
          )}

          {/* STEP 3 CONFIRM / PAY */}
          {step === 3 && (
            <div className="space-y-4" data-testid="checkout-step-confirm">
              <div className="rounded-2xl border border-border bg-card p-5">
                <p className="text-sm text-muted-foreground">Paying <span className="font-bold text-foreground">{money(total, "USD")}</span> with{" "}
                  <span className="font-bold text-foreground">{method === "wallet" ? "iYeneh Wallet" : method === "card" ? "Card" : "PayPal"}</span>.</p>
              </div>

              {method === "wallet" && (
                <button onClick={payWallet} disabled={busy} data-testid="pay-wallet" className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-primary-foreground disabled:opacity-60">
                  {busy ? "Processing…" : `Pay ${money(total, "USD")} from Wallet`}
                </button>
              )}
              {method === "card" && (
                <button onClick={payStripe} disabled={busy} data-testid="pay-card" className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-primary-foreground disabled:opacity-60">
                  {busy ? "Redirecting…" : `Pay ${money(total, "USD")} by Card`}
                </button>
              )}
              {method === "paypal" && config?.paypal_client_id && (
                <div data-testid="pay-paypal" className="max-w-sm">
                  {!user && <p className="mb-2 text-sm text-amber-500">Please <button onClick={requireAuth} className="font-bold underline">sign in</button> before paying with PayPal.</p>}
                  <PayPalScriptProvider options={{ clientId: config.paypal_client_id, currency: "USD", intent: "capture" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", shape: "pill", color: "gold" }}
                      disabled={!user || busy}
                      createOrder={async () => {
                        const { data } = await api.post("/telecom/checkout/paypal/create", { items, shipping: needAddr ? ship : null });
                        return data.order_id;
                      }}
                      onApprove={async (data) => {
                        setBusy(true);
                        try {
                          const { data: d } = await api.post(`/telecom/checkout/paypal/capture/${data.orderID}`);
                          if (d.ok) setDone({ reference: d.reference }); else toast.error("Payment was not completed.");
                        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
                      }}
                      onError={() => toast.error("PayPal payment failed. Please try again.")}
                    />
                  </PayPalScriptProvider>
                </div>
              )}
              <div>
                <button onClick={() => setStep(2)} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> Back to payment method</button>
              </div>
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck size={14} className="text-primary" /> Encrypted, secure checkout.</p>
            </div>
          )}
        </div>

        <div className="lg:sticky lg:top-24 lg:self-start"><Summary /></div>
      </div>
    </Shell>
  );
}
